import { useCallback, useEffect, useState } from 'react';
import { Icon, useToast, VStack, Flex, Text } from '@cad-arc';
import { UserExport } from '@mark43/cad-api';
import { useNonExpiredExports } from '../../../exports/use-non-expired-exports';
import { useExportNotifications } from '../../../exports/use-exports-notifications';
import {
    isDownloadingMultipleFilesSupported,
    downloadFile,
    downloadMultipleFiles,
    zipAndDownloadFiles,
} from '../../helpers/downloads';
import { ExportListItem } from '../../../exports/components/export-item';
import { ExportCard } from './export-card';
import { ExportDrawer } from './export-drawer';
import { useApplicationType } from '../../hooks/use-application-type';
import { CommonSize } from '../../helpers/utils';
import { useEnabledFeatures } from '../../hooks/enabled-features/use-enabled-features';
import { useBootstrapState } from '~/bootstrap/bootstrap-provider';
import { getFilePath, isExportExpired, isPresignedUrlExport } from '../../helpers/files';

export type ExportProps = {
    body: JSX.Element;
    currentExports: UserExport[];
    onClearAll: () => void;
    onDownloadAll: () => void;
    isLoading: boolean;
    size?: CommonSize;
};

export function Export({ isDrawer = false }: { isDrawer?: boolean }) {
    const { currentExports, isLoading, deleteExport, deleteAllExports } = useNonExpiredExports();
    const { downloadAllExportsIndividuallyEnabled, mediaAttachmentsEnabled } = useEnabledFeatures();

    const bootstrapState = useBootstrapState();
    const { events } = useExportNotifications(currentExports);
    const { isFirstResponder } = useApplicationType();
    const toast = useToast();

    const batchSize = bootstrapState.electronDownloadAllExportsBatchSize ?? 5;

    const noExportsPresent = currentExports.length === 0;

    const size = isFirstResponder ? 'lg' : 'md';

    // State to force re-renders every 60 seconds when tray is open
    // when using exports that expire in 50 minutes
    const [, setTimerTick] = useState(0);

    // Timer effect: updates every 60 seconds to refresh expiration labels
    // when using exports that expire in 50 minutes
    useEffect(
        function updateExpirationTimer() {
            // Only run timer if there are exports to display
            if (noExportsPresent) {
                return;
            }

            const hasPresignedUrl = currentExports.some((ex) =>
                isPresignedUrlExport(ex.file, mediaAttachmentsEnabled)
            );

            // Only run timer if we have exports that expire in 50 minutes
            if (!hasPresignedUrl) {
                return;
            }

            // Set up 60-second interval to force re-renders
            const intervalId = setInterval(() => {
                setTimerTick((tick) => tick + 1);
            }, 60_000); // 60 seconds

            // Cleanup interval on unmount or when dependencies change
            return () => {
                clearInterval(intervalId);
            };
        },
        [currentExports, noExportsPresent, mediaAttachmentsEnabled]
    );

    const onDownload = useCallback(
        (userExport: UserExport) => {
            // Skip if download URL has expired
            if (isExportExpired(userExport, mediaAttachmentsEnabled)) {
                toast({
                    status: 'error',
                    description: 'Export has expired. Generate a new export to download.',
                });
                // Force UI update to show expired state
                setTimerTick((tick) => tick + 1);
                return;
            }

            const isReadyForDownload =
                userExport.expirationDate && getFilePath(userExport.file, mediaAttachmentsEnabled);

            if (isReadyForDownload) {
                events.onDownloadExport(userExport.id);
                toast({ status: 'default', description: `Downloading ${userExport.title}` });
                downloadFile(userExport.file, mediaAttachmentsEnabled);
            }
        },
        [events, toast, mediaAttachmentsEnabled]
    );

    const onDownloadAll = useCallback(() => {
        events.onDownloadAllExports();

        const downloadList = currentExports.filter((e) => {
            // excluding attachments from being zipped as large attachments can crash electron or freeze web
            if (
                e.file?.fileType?.toLowerCase() === 'zip' &&
                !downloadAllExportsIndividuallyEnabled
            ) {
                return false;
            }

            // Skip exports with expired download URLs
            if (isExportExpired(e, mediaAttachmentsEnabled)) {
                return false;
            }

            const isReadyForDownload =
                e.expirationDate && getFilePath(e.file, mediaAttachmentsEnabled);

            return isReadyForDownload;
        });
        const isDownloadingMultipleFiles = downloadList.length > 1;

        if (isDownloadingMultipleFiles) {
            const description = downloadAllExportsIndividuallyEnabled
                ? `Downloading ${downloadList.length} files`
                : `Downloading ${downloadList.length} files as CADExports.zip`;

            // Use batch download if attachments feature is enabled, otherwise fall back to zip
            if (downloadAllExportsIndividuallyEnabled) {
                if (isDownloadingMultipleFilesSupported()) {
                    toast({
                        status: 'default',
                        description,
                    });
                    downloadMultipleFiles(downloadList, batchSize, mediaAttachmentsEnabled);
                } else {
                    toast({
                        status: 'error',
                        description:
                            'Download All requires a newer version of CAD Electron. Please work with your IT team to update.',
                    });
                }
            } else {
                toast({
                    status: 'default',
                    description,
                });
                zipAndDownloadFiles(downloadList, mediaAttachmentsEnabled);
            }
        } else if (downloadList.length) {
            onDownload(downloadList[0]);
        }
    }, [
        currentExports,
        events,
        onDownload,
        toast,
        batchSize,
        downloadAllExportsIndividuallyEnabled,
        mediaAttachmentsEnabled,
    ]);

    const onRemoveExport = useCallback(
        (e: UserExport) => {
            const canClear =
                (e.expirationDate && getFilePath(e.file, mediaAttachmentsEnabled)) || e.failedPrint;
            if (canClear) {
                events.onClearExport(e.id);
                deleteExport(e);
            }
        },
        [deleteExport, events, mediaAttachmentsEnabled]
    );

    const onClearAll = useCallback(() => {
        events.onClearAllExports();
        deleteAllExports(mediaAttachmentsEnabled);
    }, [deleteAllExports, events, mediaAttachmentsEnabled]);

    const mainContent = (
        <>
            {noExportsPresent ? (
                <VStack h="100%" w="100%" alignItems="center" justifyContent="center">
                    <Icon icon="Export" size="sm" />
                    <Text variant="headingXs">No exports</Text>
                </VStack>
            ) : (
                <Flex direction="column" h="100%" gap={2}>
                    {currentExports.map((ex) => (
                        <ExportListItem
                            key={ex.id}
                            item={ex}
                            onClickExport={onDownload}
                            onRemoveExport={onRemoveExport}
                            size={size}
                        />
                    ))}
                </Flex>
            )}
        </>
    );

    const ExportComponent = isDrawer ? ExportDrawer : ExportCard;

    return (
        <ExportComponent
            body={mainContent}
            currentExports={currentExports}
            onClearAll={onClearAll}
            onDownloadAll={onDownloadAll}
            isLoading={isLoading}
            size={size}
        />
    );
}
